import React, { useState, useEffect } from 'react';
import apiService from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { useRefresh } from '../../contexts/RefreshContext';
import StatsCards from './StatsCards';
import {
  LeadInsightsChart,
  UserVendorChart,
  OrdersChart,
  UsersPerDayChart,
  UnlockPerDayChart,
  SourceAnalyticsChart
} from './DashboardCharts';
import RecentVendors from './RecentVendors';
import ActiveVendors from './ActiveVendors';

const Dashboard = () => {
  const { user, hasPermission } = useAuth();
  const { setOnRefresh, clearOnRefresh } = useRefresh();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [chartData, setChartData] = useState(null);
  
  const [dateRanges, setDateRanges] = useState({
    leadStart: '',
    leadEnd: '',
    userVendorStart: '',
    userVendorEnd: '',
    userStart: '',
    userEnd: '',
    unlockStart: '',
    unlockEnd: '',
    sourceStart: '',
    sourceEnd: ''
  });

  useEffect(() => {
    const today = new Date();
    const oneMonthAgo = new Date(today);
    oneMonthAgo.setMonth(today.getMonth() - 1);
    const todayStr = today.toISOString().split('T')[0];
    const oneMonthAgoStr = oneMonthAgo.toISOString().split('T')[0];
    
    setDateRanges({
      leadStart: oneMonthAgoStr,
      leadEnd: todayStr,
      userVendorStart: oneMonthAgoStr,
      userVendorEnd: todayStr,
      userStart: oneMonthAgoStr,
      userEnd: todayStr,
      unlockStart: oneMonthAgoStr,
      unlockEnd: todayStr,
      sourceStart: oneMonthAgoStr,
      sourceEnd: todayStr
    });
  }, []);

  useEffect(() => {
    fetchDashboardData();
    setOnRefresh(handleRefresh);
    return () => clearOnRefresh();
  }, []);

  useEffect(() => {
    if (dateRanges.leadStart) {
      fetchChartData();
    }
  }, [dateRanges]);

  const handleRefresh = async () => {
    await fetchDashboardData();
    await fetchChartData();
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const dashboardResponse = await apiService.get('dashboard');
      if (dashboardResponse) {
        setDashboardData(dashboardResponse.data || dashboardResponse);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      const chartResponse = await apiService.get('leadGraphV1', {
        params: {
          lead_start_date: dateRanges.leadStart,
          lead_end_date: dateRanges.leadEnd,
          unlock_start_date: dateRanges.unlockStart,
          unlock_end_date: dateRanges.unlockEnd,
          user_vendor_start_date: dateRanges.userVendorStart,
          user_vendor_end_date: dateRanges.userVendorEnd,
          user_start_date: dateRanges.userStart,
          user_end_date: dateRanges.userEnd,
          source_start_date: dateRanges.sourceStart,
          source_end_date: dateRanges.sourceEnd
        }
      });
      
      if (chartResponse) {
        setChartData(chartResponse);
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  const handleDateChange = (field, value) => {
    setDateRanges(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009ef7] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="p-0">
        {/* Stats Cards */}
        <StatsCards dashboardData={dashboardData} hasPermission={hasPermission} user={user} />

        {/* Charts Section */}
        {hasPermission('dashboard', 'dashboard', 'view') && (
          <>
            {hasPermission('dashboard', 'lead_graph', 'view') && chartData?.data && (
              <LeadInsightsChart
                data={chartData.data}
                dashboardData={dashboardData}
                dateRange={{ start: dateRanges.leadStart, end: dateRanges.leadEnd }}
                onDateChange={handleDateChange}
              />
            )}

            {hasPermission('dashboard', 'user_vendor_graph', 'view') && chartData?.userVendorData && (
              <UserVendorChart
                data={chartData.userVendorData}
                dashboardData={dashboardData}
                dateRange={{ start: dateRanges.userVendorStart, end: dateRanges.userVendorEnd }}
                onDateChange={handleDateChange}
              />
            )}

            {hasPermission('dashboard', 'order_graph', 'view') && chartData?.revenueResult && (
              <OrdersChart data={chartData.revenueResult} dashboardData={dashboardData} />
            )}

            {hasPermission('dashboard', 'source_graph', 'view') && chartData?.sourceChartData && (
              <SourceAnalyticsChart
                data={chartData.sourceChartData}
                dateRange={{ start: dateRanges.sourceStart, end: dateRanges.sourceEnd }}
                onDateChange={handleDateChange}
              />
            )}

            {hasPermission('dashboard', 'user_graph', 'view') && chartData?.userData && (
              <UsersPerDayChart
                data={chartData.userData}
                dashboardData={dashboardData}
                dateRange={{ start: dateRanges.userStart, end: dateRanges.userEnd }}
                onDateChange={handleDateChange}
              />
            )}

            {hasPermission('dashboard', 'unlock_graph', 'view') && chartData?.unlockGraphData && (
              <UnlockPerDayChart
                data={chartData.unlockGraphData}
                dashboardData={dashboardData}
                dateRange={{ start: dateRanges.unlockStart, end: dateRanges.unlockEnd }}
                onDateChange={handleDateChange}
              />
            )}

            {hasPermission('dashboard', 'recent_vendor', 'view') && dashboardData?.vendors && Array.isArray(dashboardData.vendors) && dashboardData.vendors.length > 0 && (
              <RecentVendors vendors={dashboardData.vendors} todayVendors={dashboardData.todayVendors || 0} />
            )}

            {hasPermission('dashboard', 'recent_vendor', 'view') && (
              <ActiveVendors />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;


